import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseDataProps } from './types';
import { CheckBoxListQcFieldName } from './const';
import { Choice, FieldDesc, ComboDesc, AllFieldValueTypes } from '90.quickConnect.Models/models';
import { useStore } from '30.quickConnect.Stores';
import { useQCUpdateFunctionsContext } from '20.formLib/DeclarationContainer/contexts';
import { isApplicativeListId } from '80.quickConnect.Core/const/applicativeListIds';

const useData = (
  field: FieldDesc,
  updateDeclaration: (updatedFieldFullPathId: string, newValue: AllFieldValueTypes) => void,
  setSelectedIndex: Dispatch<SetStateAction<string | undefined>>,
): UseDataProps => {
  // On récupère le store
  const {
    SharedListStore: { getSharedList },
  } = useStore();

  const { updateProperty } = useQCUpdateFunctionsContext();

  // On récupère la translation
  const { t } = useTranslation('declaration');
  const comboDesc = useMemo(() => field as ComboDesc, [field]);
  const { listId, listChoice, id, toolTip, value, fullPathId, filterDef } = comboDesc;
  const nameWithId = `${CheckBoxListQcFieldName}-${id}`;
  const [isDeleteAll, setIsDeleteAll] = useState<boolean>(false);
  // undefined = en cours de chargement, [] = aucun choix
  const [choices, setChoices] = useState<Choice[] | undefined>(listChoice);
  const [isItemDeleted, setIsItemDeleted] = useState<string>();

  const deleteItem = useCallback(
    (choice: Choice) => {
      // TODO: Faire un reduce
      const filteredValue = (value as Choice[]).filter(
        (it) => !(it.value === choice.value && it.label === choice.label),
      );
      updateDeclaration(fullPathId, filteredValue);
      setIsItemDeleted(choice.label ?? choice.value);
      setSelectedIndex(undefined);
    },
    [fullPathId, setSelectedIndex, updateDeclaration, value],
  );

  const handleDeleteAll = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      e.stopPropagation();
      updateDeclaration(fullPathId, []);
      setIsDeleteAll(true);
      setSelectedIndex(undefined);
    },
    [fullPathId, setSelectedIndex, updateDeclaration],
  );
  // Récupération des choices
  const lookedForList = useRef(false);

  useEffect(() => {
    if (listChoice && listChoice.length > 0) {
      setChoices(listChoice);
      lookedForList.current = true;
      return;
    }

    // Listes applicatives (##documents, ##declaration) : remplies à l'hydratation, jamais présentes dans les listes partagées
    if (isApplicativeListId(listId)) {
      setChoices(listChoice ?? []);
      return;
    }

    if (listId && lookedForList.current === false) {
      getSharedList(listId).then((sharedList) => {
        if (sharedList) {
          setChoices(listChoice ?? sharedList.data);
          updateProperty(fullPathId, 'listChoice', listChoice ?? sharedList.data);
        } else {
          // Liste introuvable : aucun choix
          setChoices([]);
        }
      });
      lookedForList.current = true;
    }
  }, [listId, getSharedList, updateProperty, fullPathId, listChoice]);

  useEffect(() => {
    if (listChoice && filterDef) {
      setChoices(listChoice);
    }
  }, [filterDef, listChoice]);

  // Calcul des valeurs filtrées (présentes dans choices)
  let filteredValues: Choice[] = [];
  if (Array.isArray(field.value) && Array.isArray(choices)) {
    filteredValues = (field.value as Choice[])
      .map((v) => {
        const found = choices.find((choice) => choice.value === v.value);
        if (found) {
          return { value: found.value, label: found.label };
        }
        return undefined;
      })
      .filter((v): v is Choice => !!v);
  }

  // On retourne les valeurs à la vue
  return {
    t,
    choices,
    id,
    nameWithId,
    toolTip,
    deleteItem,
    isItemDeleted,
    setIsItemDeleted,
    handleDeleteAll,
    isDeleteAll,
    setIsDeleteAll,
    filteredValues,
  };
};

export default useData;
